import { CATALOG, randomAppearance, sameAppearance, withDefaults, type ColorOption, type PartKind } from "../avatar/catalog";
import { avatarPartsReady, drawFrame } from "../avatar/composer";
import type { Appearance } from "../types";
import { requireElement } from "./Hud";

/** Linhas do spritesheet, na ordem em que a previa gira ao clicar nas setas. */
const TURN_ORDER = [0, 2, 3, 1]; // down, right, up, left
/** Ciclo de caminhada: contato, passagem, contato, passagem (colunas 1..4). */
const WALK_FRAMES = [1, 2, 3, 4];
const PREVIEW_SCALE = 4;
const THUMB_SCALE = 2;

/** Abas do editor, como as categorias do Gather. */
const TABS = [
  { id: "skin", label: "Pele" },
  { id: "hair", label: "Cabelo" },
  { id: "face", label: "Rosto" },
  { id: "clothes", label: "Roupas" },
  { id: "shoes", label: "Calçados" },
  { id: "extras", label: "Acessórios" },
] as const;

type Tab = (typeof TABS)[number]["id"];

/** Recorte de cada miniatura: so a regiao do frame onde a parte aparece. */
const CROPS: Record<PartKind, { y: number; h: number }> = {
  hair: { y: 0, h: 30 },
  facialHair: { y: 8, h: 22 },
  glasses: { y: 6, h: 20 },
  earrings: { y: 8, h: 22 },
  headwear: { y: 0, h: 26 },
  top: { y: 20, h: 26 },
  bottom: { y: 30, h: 18 },
  shoes: { y: 34, h: 14 },
};

export interface CustomizerOptions {
  initial: Appearance;
  /** Primeira vez (conta sem avatar): textos de boas-vindas e botao "Agora nao". */
  firstTime: boolean;
  /** Grava no servidor; um erro lancado aparece no painel e mantem ele aberto. */
  save: (appearance: Appearance) => Promise<void>;
}

/**
 * Editor de avatar no estilo do Gather: previa grande andando, abas por parte
 * e miniaturas ja com as cores escolhidas. Resolve com a aparencia salva, ou
 * null se o jogador fechou sem salvar.
 */
export function showAvatarCustomizer({ initial, firstTime, save }: CustomizerOptions): Promise<Appearance | null> {
  return new Promise((resolve, reject) => {
    if (!avatarPartsReady()) {
      reject(new Error("partes do avatar não carregadas"));
      return;
    }
    const root = requireElement<HTMLDivElement>("overlay-root");
    const overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.innerHTML = template(firstTime);
    root.replaceChildren(overlay);

    const $ = <T extends HTMLElement>(selector: string) => overlay.querySelector(selector) as T;

    let look: Appearance = withDefaults(initial);
    let turn = 0;
    let step = 0;
    let busy = false;

    // ------------------------------------------------------------ previa
    const { width, height, cols } = CATALOG.frame;
    const preview = $<HTMLCanvasElement>("#custom-preview");
    preview.width = width;
    preview.height = height;
    preview.style.width = `${width * PREVIEW_SCALE}px`;
    preview.style.height = `${height * PREVIEW_SCALE}px`;

    // Qualquer falha de desenho fecha o painel e rejeita: quem chamou libera o jogo.
    const fail = (error: unknown) => {
      window.clearInterval(walk);
      document.removeEventListener("keydown", onKey);
      root.replaceChildren();
      reject(error);
    };
    const drawPreview = () => {
      try {
        drawFrame(preview, look, TURN_ORDER[turn] * cols + WALK_FRAMES[step]);
      } catch (error) {
        fail(error);
      }
    };
    const walk = window.setInterval(() => {
      step = (step + 1) % WALK_FRAMES.length;
      drawPreview();
    }, 140);

    $<HTMLButtonElement>("#turn-left").addEventListener("click", () => {
      turn = (turn + TURN_ORDER.length - 1) % TURN_ORDER.length;
      drawPreview();
    });
    $<HTMLButtonElement>("#turn-right").addEventListener("click", () => {
      turn = (turn + 1) % TURN_ORDER.length;
      drawPreview();
    });

    // ------------------------------------------------------------- abas
    let tab: Tab = "skin";
    const body = $<HTMLDivElement>("#custom-body");
    overlay.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((btn) =>
      btn.addEventListener("click", () => {
        tab = btn.dataset.tab as Tab;
        overlay
          .querySelectorAll<HTMLButtonElement>("[data-tab]")
          .forEach((b) => b.setAttribute("aria-selected", String(b === btn)));
        safeRenderBody();
      }),
    );

    const update = (patch: Partial<Appearance>) => {
      look = { ...look, ...patch };
      drawPreview();
      safeRenderBody();
    };
    const safeRenderBody = () => {
      try {
        renderBody();
      } catch (error) {
        fail(error);
      }
    };

    const section = (title: string, hint?: string) => {
      const h = document.createElement("h2");
      h.className = "custom-section";
      h.textContent = title;
      body.append(h);
      if (hint) {
        const p = document.createElement("p");
        p.className = "custom-hint";
        p.textContent = hint;
        body.append(p);
      }
    };

    /**
     * Fileira de cores. `disabled` deixa a fileira apagada mas no lugar (cor
     * secundaria de peca lisa, cor de acessorio "nenhum"), para a grade de
     * pecas nao pular quando ela passa a valer.
     */
    const swatches = (colors: ColorOption[], selected: number, onPick: (i: number) => void, label?: string, disabled = false) => {
      const row = document.createElement("div");
      row.className = "swatch-row" + (disabled ? " disabled" : "");
      if (label) {
        const tag = document.createElement("span");
        tag.className = "swatch-label";
        tag.textContent = label;
        row.append(tag);
      }
      colors.forEach((c, i) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "swatch" + (i === selected && !disabled ? " selected" : "");
        b.style.background = c.hex;
        b.disabled = disabled;
        b.setAttribute("aria-label", `${label ?? "Cor"} ${i + 1}`);
        b.addEventListener("click", () => onPick(i));
        row.append(b);
      });
      body.append(row);
    };

    const styles = (kind: PartKind, selected: string, onPick: (id: string) => void) => {
      const grid = document.createElement("div");
      grid.className = "style-grid";
      const crop = CROPS[kind];
      for (const part of CATALOG.parts[kind]) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "style-option" + (part.id === selected ? " selected" : "");
        b.title = part.label;
        const thumb = document.createElement("canvas");
        thumb.width = width;
        thumb.height = crop.h;
        thumb.style.width = `${width * THUMB_SCALE}px`;
        thumb.style.height = `${crop.h * THUMB_SCALE}px`;
        drawFrame(thumb, { ...look, [kind]: part.id }, 0, crop);
        const label = document.createElement("span");
        label.textContent = part.label;
        b.append(thumb, label);
        b.addEventListener("click", () => onPick(part.id));
        grid.append(b);
      }
      body.append(grid);
    };

    const renderBody = () => {
      body.replaceChildren();
      const { skin, hair, cloth } = CATALOG.colors;
      const pick = (kind: PartKind) => (id: string) => update({ [kind]: id });
      switch (tab) {
        case "skin":
          section("Tom de pele");
          swatches(skin, look.skinTone, (i) => update({ skinTone: i }));
          break;
        case "hair":
          section("Cabelo");
          swatches(hair, look.hairColor, (i) => update({ hairColor: i }));
          styles("hair", look.hair, pick("hair"));
          break;
        case "face":
          section("Barba e bigode", "Usa a cor do cabelo.");
          styles("facialHair", look.facialHair, pick("facialHair"));
          section("Óculos");
          styles("glasses", look.glasses, pick("glasses"));
          break;
        case "clothes": {
          const twoTone = CATALOG.parts.top.find((p) => p.id === look.top)?.twoTone ?? false;
          section("Parte de cima");
          swatches(cloth, look.topColor, (i) => update({ topColor: i }), "Cor");
          swatches(cloth, look.topColor2, (i) => update({ topColor2: i }), "Detalhe", !twoTone);
          styles("top", look.top, pick("top"));
          section("Parte de baixo");
          swatches(cloth, look.bottomColor, (i) => update({ bottomColor: i }));
          styles("bottom", look.bottom, pick("bottom"));
          break;
        }
        case "shoes":
          section("Calçados");
          swatches(cloth, look.shoesColor, (i) => update({ shoesColor: i }));
          styles("shoes", look.shoes, pick("shoes"));
          break;
        case "extras":
          section("Na cabeça");
          swatches(cloth, look.headwearColor, (i) => update({ headwearColor: i }), "Cor", look.headwear === CATALOG.none);
          styles("headwear", look.headwear, pick("headwear"));
          section("Brincos");
          styles("earrings", look.earrings, pick("earrings"));
          break;
      }
    };

    // ---------------------------------------------------------- acoes
    const notice = $<HTMLParagraphElement>(".notice");
    const saveBtn = $<HTMLButtonElement>("#custom-save");

    const finish = (result: Appearance | null) => {
      window.clearInterval(walk);
      document.removeEventListener("keydown", onKey);
      root.replaceChildren();
      resolve(result);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        event.preventDefault();
        finish(null);
      }
    };
    document.addEventListener("keydown", onKey);

    $<HTMLButtonElement>("#custom-random").addEventListener("click", () => update(randomAppearance()));
    $<HTMLButtonElement>("#custom-cancel").addEventListener("click", () => !busy && finish(null));
    saveBtn.addEventListener("click", async () => {
      if (busy) return;
      if (!firstTime && sameAppearance(look, initial)) {
        finish(null);
        return;
      }
      busy = true;
      saveBtn.disabled = true;
      notice.classList.add("hidden");
      try {
        await save(look);
        finish(look);
      } catch (error) {
        notice.className = "notice error";
        notice.textContent = error instanceof Error ? error.message : "Não foi possível salvar o avatar";
        busy = false;
        saveBtn.disabled = false;
      }
    });

    drawPreview();
    safeRenderBody();
  });
}

function template(firstTime: boolean): string {
  return `
    <div class="panel customizer">
      <h1>${firstTime ? "Monte seu avatar" : "Editar avatar"}</h1>
      <p class="sub">${
        firstTime
          ? "A cidade ganhou cara nova! Escolha como você quer aparecer na Praça da Matriz."
          : "As mudanças aparecem para todo mundo assim que você salvar."
      }</p>
      <p class="notice hidden"></p>

      <div class="custom-layout">
        <div class="custom-stage">
          <canvas id="custom-preview" aria-label="Prévia do avatar"></canvas>
          <div class="custom-turn">
            <button type="button" id="turn-left" class="secondary small" aria-label="Girar para a esquerda">◀</button>
            <button type="button" id="custom-random" class="secondary small">Aleatório</button>
            <button type="button" id="turn-right" class="secondary small" aria-label="Girar para a direita">▶</button>
          </div>
        </div>

        <div class="custom-editor">
          <div class="tabs custom-tabs" role="tablist">
            ${TABS.map(
              (t, i) => `<button type="button" role="tab" data-tab="${t.id}" aria-selected="${i === 0}">${t.label}</button>`,
            ).join("")}
          </div>
          <div id="custom-body" class="custom-body"></div>
        </div>
      </div>

      <div class="custom-actions">
        <button type="button" id="custom-cancel" class="secondary">${firstTime ? "Agora não" : "Cancelar"}</button>
        <button type="button" id="custom-save" class="primary">${firstTime ? "Pronto!" : "Salvar"}</button>
      </div>
    </div>
  `;
}
